/**
 * LBRN Import utilities
 * Parse LightBurn (.lbrn, .lbrn2) XML files into TemplateShapes
 */
import type { TemplateShape } from '../types/dbTypes';
import type { ImportResult } from './svgImport';

// ============================================================
// LightBurn Layer Colors (C00-C29, T1, T2)
// Official colors from LightBurn software
// Source: https://softsolder.com/2025/04/11/inkscape-lightburn-layer-color-palette/
// ============================================================
const LIGHTBURN_COLORS: Record<string, string> = {
    // Standard layers C00-C29
    'C00': '#000000',  // Black
    'C01': '#0000FF',  // Blue
    'C02': '#FF0000',  // Red
    'C03': '#00E000',  // Green
    'C04': '#D0D000',  // Yellow/Olive
    'C05': '#FF8000',  // Orange
    'C06': '#00E0E0',  // Cyan
    'C07': '#FF00FF',  // Magenta
    'C08': '#B4B4B4',  // Gray
    'C09': '#0000A0',  // Dark Blue
    'C10': '#A00000',  // Dark Red
    'C11': '#00A000',  // Dark Green
    'C12': '#A0A000',  // Olive
    'C13': '#C08000',  // Brown
    'C14': '#00A0FF',  // Light Blue
    'C15': '#A000A0',  // Purple
    'C16': '#808080',  // Medium Gray
    'C17': '#7D87B9',  // Slate Blue
    'C18': '#BB7784',  // Dusty Rose
    'C19': '#4A6FE3',  // Royal Blue
    'C20': '#D33F6A',  // Raspberry
    'C21': '#8CD78C',  // Light Green
    'C22': '#F0B98D',  // Peach
    'C23': '#F6C4E1',  // Light Pink
    'C24': '#FA9ED4',  // Pink
    'C25': '#500A78',  // Deep Purple
    'C26': '#B45A00',  // Rust
    'C27': '#004754',  // Dark Teal
    'C28': '#86FA88',  // Bright Green
    'C29': '#FFDB66',  // Gold
    // Tool layers
    'T1': '#F36926',   // Orange (Tool 1)
    'T2': '#0C96D9',   // Sky Blue (Tool 2)
};

// Default color for fallback
const LBRN_COLOR_BLACK = '#000000';

// ============================================================
// LBRN File Structure Interfaces
// ============================================================

export interface LbrnCutSetting {
    // Core settings
    index: number;
    type: 'Cut' | 'Scan' | 'Image' | string;
    name: string;
    maxPower: number;
    minPower?: number;
    speed: number;
    numPasses: number;
    priority?: number;
    doOutput?: boolean;

    // Z-Axis
    zOffset?: number;
    zPerPass?: number;

    // Laser Control
    frequency?: number;
    overrideFrequency?: boolean;

    // Air Assist
    runBlower?: boolean;

    // Cut-Through
    enableCutThrough?: boolean;
    throughPower?: number;

    // Kerf
    kerf?: number;

    // Overcut & Ramp
    overcut?: number;
    rampLength?: number;

    // Perforation
    perforate?: boolean;
    perfLen?: number;
    perfSkip?: number;

    // Dot Mode
    dotMode?: boolean;
    dotTime?: number;
    dotSpacing?: number;

    // Lead In/Out
    leadInType?: number;   // 0=line, 1=arc
    leadInLength?: number;
    leadInAngle?: number;
    leadOutType?: number;
    leadOutLength?: number;
    leadOutAngle?: number;

    // Scan/Fill Settings
    interval?: number;
    bidir?: boolean;
    overscan?: number;
    angle?: number;
    crossHatch?: boolean;
    floodFill?: boolean;
}

interface LbrnVertex {
    vx: number;
    vy: number;
    c0x?: number;  // Outgoing control point
    c0y?: number;
    c1x?: number;  // Incoming control point
    c1y?: number;
}

interface LbrnPrimitive {
    type: 'L' | 'B' | 'Q' | string;
    p0: number;
    p1: number;
}

interface LbrnXForm {
    a: number;
    b: number;
    c: number;
    d: number;
    tx: number;
    ty: number;
}

interface PathBounds {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
    width: number;
    height: number;
}

// ============================================================
// Extended ImportResult
// ============================================================

export interface LbrnImportResult extends ImportResult {
    warnings: string[];
    cutSettings?: Map<number, LbrnCutSetting>;
}

// ============================================================
// Layer Color Mapping
// ============================================================

function layerToColor(layerName: string): string {
    return LIGHTBURN_COLORS[layerName] || LBRN_COLOR_BLACK;
}

// ============================================================
// Operation Mapping
// ============================================================

function mapCutSettingToOperations(
    cutSetting: LbrnCutSetting | undefined,
    layerName: string
): {
    score: boolean;
    engrave: boolean;
    cut: boolean;
    scoreColor: string;
    engraveColor: string;
    cutColor: string;
} {
    const color = layerToColor(layerName);

    if (!cutSetting) {
        // Default to engrave with layer color
        return {
            score: false,
            engrave: true,
            cut: false,
            scoreColor: color,
            engraveColor: color,
            cutColor: color,
        };
    }

    // In LightBurn, there is no "score" - only Scan (engrave) and Cut
    // Map the operation type and use the layer's color for the active operation
    switch (cutSetting.type) {
        case 'Scan':
        case 'Image':
            // Raster/engrave operations
            return {
                score: false,
                engrave: true,
                cut: false,
                scoreColor: color,
                engraveColor: color,
                cutColor: color,
            };
        case 'Cut':
            // Vector cut operations
            return {
                score: false,
                engrave: false,
                cut: true,
                scoreColor: color,
                engraveColor: color,
                cutColor: color,
            };
        default:
            // Default to engrave with layer color
            return {
                score: false,
                engrave: true,
                cut: false,
                scoreColor: color,
                engraveColor: color,
                cutColor: color,
            };
    }
}

// ============================================================
// CutSettings Parser
// ============================================================

function parseCutSettings(doc: Document): Map<number, LbrnCutSetting> {
    const settings = new Map<number, LbrnCutSetting>();

    // Helper to parse a CutSetting or CutSetting_Img element
    const parseCutSettingElement = (cs: Element) => {
        // Helper functions for parsing XML element values
        const getValue = (name: string): string | null => {
            const el = cs.querySelector(name);
            return el?.getAttribute('Value') ?? null;
        };
        const getFloat = (name: string, def: number): number => {
            const v = getValue(name);
            return v !== null ? parseFloat(v) : def;
        };
        const getInt = (name: string, def: number): number => {
            const v = getValue(name);
            return v !== null ? parseInt(v) : def;
        };
        const getBool = (name: string): boolean | undefined => {
            const v = getValue(name);
            return v !== null ? v === '1' || v === 'true' : undefined;
        };
        const getOptFloat = (name: string): number | undefined => {
            const v = getValue(name);
            if (v === null) return undefined;
            const num = parseFloat(v);
            return num !== 0 ? num : undefined;
        };
        const getOptInt = (name: string): number | undefined => {
            const v = getValue(name);
            if (v === null) return undefined;
            const num = parseInt(v);
            return !isNaN(num) ? num : undefined;
        };

        const index = getInt('index', 0);
        const type = cs.getAttribute('type') || 'Cut';

        settings.set(index, {
            // Core settings
            index,
            type,
            name: getValue('name') || `C${index.toString().padStart(2, '0')}`,
            maxPower: getFloat('maxPower', 100),
            minPower: getOptFloat('minPower'),
            speed: getFloat('speed', 100),
            numPasses: getInt('numPasses', 1),
            priority: getOptInt('priority'),
            doOutput: getBool('doOutput'),

            // Z-Axis
            zOffset: getOptFloat('zOffset'),
            zPerPass: getOptFloat('zPerPass'),

            // Laser Control
            frequency: getOptInt('frequency'),
            overrideFrequency: getBool('overrideFrequency'),

            // Air Assist
            runBlower: getBool('runBlower'),

            // Cut-Through
            enableCutThrough: getBool('enableCutThrough'),
            throughPower: getOptFloat('throughPower'),

            // Kerf
            kerf: getOptFloat('kerf'),

            // Overcut & Ramp
            overcut: getOptFloat('overcut'),
            rampLength: getOptFloat('rampLength'),

            // Perforation
            perforate: getBool('perforate'),
            perfLen: getOptFloat('perfLen'),
            perfSkip: getOptFloat('perfSkip'),

            // Dot Mode
            dotMode: getBool('dotMode'),
            dotTime: getOptFloat('dotTime'),
            dotSpacing: getOptFloat('dotSpacing'),

            // Lead In/Out
            leadInType: getOptInt('leadInType'),
            leadInLength: getOptFloat('leadInLength'),
            leadInAngle: getOptFloat('leadInAngle'),
            leadOutType: getOptInt('leadOutType'),
            leadOutLength: getOptFloat('leadOutLength'),
            leadOutAngle: getOptFloat('leadOutAngle'),

            // Scan Settings
            interval: getOptFloat('interval'),
            bidir: getBool('bidir'),
            overscan: getOptFloat('overscan'),
            angle: getOptFloat('angle'),
            crossHatch: getBool('crossHatch'),
            floodFill: getBool('floodFill'),
        });
    };

    // Parse regular CutSetting elements
    doc.querySelectorAll('CutSetting').forEach(parseCutSettingElement);

    // Also parse CutSetting_Img elements (for Bitmap image layers)
    doc.querySelectorAll('CutSetting_Img').forEach(parseCutSettingElement);

    return settings;
}

// ============================================================
// XForm Matrix Parsing
// ============================================================

function parseXForm(element: Element): LbrnXForm {
    const xformEl = element.querySelector(':scope > XForm');
    if (!xformEl || !xformEl.textContent) {
        return { a: 1, b: 0, c: 0, d: 1, tx: 0, ty: 0 };
    }

    const parts = xformEl.textContent.trim().split(/\s+/).map(parseFloat);
    return {
        a: parts[0] ?? 1,
        b: parts[1] ?? 0,
        c: parts[2] ?? 0,
        d: parts[3] ?? 1,
        tx: parts[4] ?? 0,
        ty: parts[5] ?? 0,
    };
}

function applyXForm(x: number, y: number, xform: LbrnXForm): { x: number; y: number } {
    return {
        x: xform.a * x + xform.c * y + xform.tx,
        y: xform.b * x + xform.d * y + xform.ty,
    };
}

// ============================================================
// Ellipse to SVG Path
// ============================================================

function ellipseToPath(cx: number, cy: number, rx: number, ry: number): string {
    const k = 0.552284749831;  // Bezier circle approximation constant
    const kx = rx * k;
    const ky = ry * k;

    // Use formatCoord for all values to ensure valid SVG
    const f = formatCoord;
    return `M ${f(cx)} ${f(cy - ry)} ` +
           `C ${f(cx + kx)} ${f(cy - ry)} ${f(cx + rx)} ${f(cy - ky)} ${f(cx + rx)} ${f(cy)} ` +
           `C ${f(cx + rx)} ${f(cy + ky)} ${f(cx + kx)} ${f(cy + ry)} ${f(cx)} ${f(cy + ry)} ` +
           `C ${f(cx - kx)} ${f(cy + ry)} ${f(cx - rx)} ${f(cy + ky)} ${f(cx - rx)} ${f(cy)} ` +
           `C ${f(cx - rx)} ${f(cy - ky)} ${f(cx - kx)} ${f(cy - ry)} ${f(cx)} ${f(cy - ry)} Z`;
}

// ============================================================
// Polygon to SVG Path
// ============================================================

function polygonToPath(cx: number, cy: number, rx: number, ry: number, n: number): string {
    if (n < 3) return '';

    const f = formatCoord;
    const points: string[] = [];

    for (let i = 0; i < n; i++) {
        // LightBurn convention:
        // - Odd-sided polygons (3,5): vertex at TOP (start at -π/2)
        // - Even-sided polygons (6,8): flat side at TOP (offset by π/n)
        const startOffset = n % 2 === 0 ? Math.PI / n : 0;
        const angle = (2 * Math.PI * i / n) - Math.PI / 2 + startOffset;
        const x = cx + rx * Math.cos(angle);
        const y = cy + ry * Math.sin(angle);

        if (i === 0) {
            points.push(`M ${f(x)} ${f(y)}`);
        } else {
            points.push(`L ${f(x)} ${f(y)}`);
        }
    }
    points.push('Z');

    return points.join(' ');
}

// ============================================================
// Star to SVG Path
// ============================================================

function starToPath(cx: number, cy: number, rx: number, ry: number, n: number, ra: number): string {
    if (n < 3) return '';

    const f = formatCoord;
    const points: string[] = [];
    const innerRx = rx * ra;
    const innerRy = ry * ra;

    // A star has 2*n vertices alternating between outer and inner points
    for (let i = 0; i < n * 2; i++) {
        // LightBurn starts from the TOP (angle = -π/2 in SVG's Y-down coords)
        const angle = (Math.PI * i / n) - Math.PI / 2;
        const isOuter = i % 2 === 0;
        const currentRx = isOuter ? rx : innerRx;
        const currentRy = isOuter ? ry : innerRy;
        const x = cx + currentRx * Math.cos(angle);
        const y = cy + currentRy * Math.sin(angle);

        if (i === 0) {
            points.push(`M ${f(x)} ${f(y)}`);
        } else {
            points.push(`L ${f(x)} ${f(y)}`);
        }
    }
    points.push('Z');

    return points.join(' ');
}

// ============================================================
// DualStar to SVG Path (star with two inner radius ratios)
// ============================================================

function dualStarToPath(cx: number, cy: number, rx: number, ry: number, n: number, ra: number, ra2: number): string {
    if (n < 3) return '';

    const f = formatCoord;
    const points: string[] = [];

    // DualStar: 4*n vertices in pattern: outer → ra → ra2 → ra → outer
    // This creates: main spike → valley → intermediate spike → valley → main spike
    // LightBurn pattern: outer tips at mod=0, intermediate tips at mod=2, valleys at mod=1,3
    for (let i = 0; i < n * 4; i++) {
        // LightBurn starts from the TOP (angle = -π/2 in SVG's Y-down coords)
        const angle = (2 * Math.PI * i / (n * 4)) - Math.PI / 2;
        let radiusRatio: number;

        const mod = i % 4;
        if (mod === 0) {
            radiusRatio = 1.0;  // Main outer spike
        } else if (mod === 2) {
            radiusRatio = ra2;  // Intermediate spike tip
        } else {  // mod === 1 or 3
            radiusRatio = ra;   // Valley points
        }

        const x = cx + rx * radiusRatio * Math.cos(angle);
        const y = cy + ry * radiusRatio * Math.sin(angle);

        if (i === 0) {
            points.push(`M ${f(x)} ${f(y)}`);
        } else {
            points.push(`L ${f(x)} ${f(y)}`);
        }
    }
    points.push('Z');

    return points.join(' ');
}

// ============================================================
// V/P to SVG Path Conversion
// ============================================================

// Helper to format coordinate with precision and validate
function formatCoord(n: number): string {
    if (!Number.isFinite(n)) return '0';
    return Number(n.toFixed(4)).toString();
}

function lbrnPathToSvg(
    vertices: LbrnVertex[],
    primitives: LbrnPrimitive[],
    xform: LbrnXForm,
    mirrorY: boolean,
    canvasHeight: number
): string {
    const commands: string[] = [];
    let currentPos: { x: number; y: number } | null = null;

    const transformPoint = (vx: number, vy: number): { x: number; y: number } => {
        let { x, y } = applyXForm(vx, vy, xform);
        if (mirrorY) {
            y = canvasHeight - y;
        }
        return { x, y };
    };

    for (const prim of primitives) {
        const startVertex = vertices[prim.p0];
        const endVertex = vertices[prim.p1];

        if (!startVertex || !endVertex) continue;

        const start = transformPoint(startVertex.vx, startVertex.vy);
        const end = transformPoint(endVertex.vx, endVertex.vy);

        // MoveTo if not continuing from last position
        if (!currentPos || Math.abs(currentPos.x - start.x) > 0.001 || Math.abs(currentPos.y - start.y) > 0.001) {
            commands.push(`M ${formatCoord(start.x)} ${formatCoord(start.y)}`);
        }

        if (prim.type === 'L') {
            commands.push(`L ${formatCoord(end.x)} ${formatCoord(end.y)}`);
        } else if (prim.type === 'B') {
            // Bezier: c0 from start vertex (outgoing), c1 from end vertex (incoming)
            const c0 = startVertex.c0x !== undefined
                ? transformPoint(startVertex.c0x, startVertex.c0y!)
                : start;
            const c1 = endVertex.c1x !== undefined
                ? transformPoint(endVertex.c1x, endVertex.c1y!)
                : end;

            commands.push(`C ${formatCoord(c0.x)} ${formatCoord(c0.y)} ${formatCoord(c1.x)} ${formatCoord(c1.y)} ${formatCoord(end.x)} ${formatCoord(end.y)}`);
        } else if (prim.type === 'Q') {
            const ctrl = startVertex.c0x !== undefined
                ? transformPoint(startVertex.c0x, startVertex.c0y!)
                : start;
            commands.push(`Q ${formatCoord(ctrl.x)} ${formatCoord(ctrl.y)} ${formatCoord(end.x)} ${formatCoord(end.y)}`);
        }

        currentPos = end;
    }

    // Close path if it returns to start
    if (primitives.length > 0 && currentPos) {
        const firstVertex = vertices[primitives[0].p0];
        if (firstVertex) {
            const first = transformPoint(firstVertex.vx, firstVertex.vy);
            if (Math.abs(first.x - currentPos.x) < 0.01 && Math.abs(first.y - currentPos.y) < 0.01) {
                commands.push('Z');
            }
        }
    }

    return commands.join(' ');
}

function calculatePathBounds(
    vertices: LbrnVertex[],
    xform: LbrnXForm,
    mirrorY: boolean,
    canvasHeight: number
): PathBounds {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    for (const v of vertices) {
        let { x, y } = applyXForm(v.vx, v.vy, xform);
        if (mirrorY) {
            y = canvasHeight - y;
        }
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);

        // Also check control points
        if (v.c0x !== undefined && v.c0y !== undefined) {
            let { x: cx, y: cy } = applyXForm(v.c0x, v.c0y, xform);
            if (mirrorY) cy = canvasHeight - cy;
            minX = Math.min(minX, cx);
            minY = Math.min(minY, cy);
            maxX = Math.max(maxX, cx);
            maxY = Math.max(maxY, cy);
        }
        if (v.c1x !== undefined && v.c1y !== undefined) {
            let { x: cx, y: cy } = applyXForm(v.c1x, v.c1y, xform);
            if (mirrorY) cy = canvasHeight - cy;
            minX = Math.min(minX, cx);
            minY = Math.min(minY, cy);
            maxX = Math.max(maxX, cx);
            maxY = Math.max(maxY, cy);
        }
    }

    return {
        minX,
        minY,
        maxX,
        maxY,
        width: maxX - minX,
        height: maxY - minY,
    };
}

// ============================================================
// Shape Parsers
// ============================================================

function parseRectShape(
    element: Element,
    cutSettings: Map<number, LbrnCutSetting>,
    mirrorY: boolean,
    canvasHeight: number,
    orderIndex: number
): Omit<TemplateShape, 'id' | 'templateId'> | null {
    const w = parseFloat(element.getAttribute('W') || '0');
    const h = parseFloat(element.getAttribute('H') || '0');
    const cutIndex = parseInt(element.getAttribute('CutIndex') || '0');

    if (w === 0 || h === 0) return null;

    const xform = parseXForm(element);

    // XForm tx,ty is the CENTER of the rect - convert to top-left
    let x = xform.tx - w / 2;
    let y = xform.ty - h / 2;

    // Flip Y if MirrorY is true
    if (mirrorY) {
        y = canvasHeight - (y + h);
    }

    const cutSetting = cutSettings.get(cutIndex);
    const ops = mapCutSettingToOperations(cutSetting, cutSetting?.name || 'C00');

    // For rects (field placeholders), ensure engrave is enabled by default
    const finalOps = { ...ops };
    if (!ops.score && !ops.engrave && !ops.cut) {
        finalOps.engrave = true;
    }

    return {
        type: 'rect',
        x,
        y,
        width: w,
        height: h,
        rotation: 0,
        orderIndex,
        fieldId: undefined,
        lbrnCutIndex: cutIndex,  // Store CutIndex for process settings import
        ...finalOps,
    } as Omit<TemplateShape, 'id' | 'templateId'>;
}

/**
 * Parse LBRN Bitmap shape into TemplateShape (raster image)
 *
 * LBRN Bitmap stores:
 * - W, H: Original image dimensions
 * - Data: Raw base64 (NO prefix - need to add data:image/png;base64,)
 * - XForm: scaleX skewX skewY scaleY tx ty (tx,ty is CENTER)
 *
 * Final display size = W * scaleX, H * scaleY
 * Position = tx - displayWidth/2, ty - displayHeight/2 (convert center to top-left)
 */
function parseBitmapShape(
    element: Element,
    cutSettings: Map<number, LbrnCutSetting>,
    mirrorY: boolean,
    canvasHeight: number,
    orderIndex: number
): Omit<TemplateShape, 'id' | 'templateId'> | null {
    const w = parseFloat(element.getAttribute('W') || '0');
    const h = parseFloat(element.getAttribute('H') || '0');
    const cutIndex = parseInt(element.getAttribute('CutIndex') || '0');
    const base64Data = element.getAttribute('Data') || '';

    if (w === 0 || h === 0 || !base64Data) return null;

    const xform = parseXForm(element);

    // Calculate actual display size by applying XForm scale
    const scaleX = Math.abs(xform.a) || 1;
    const scaleY = Math.abs(xform.d) || 1;
    const displayWidth = w * scaleX;
    const displayHeight = h * scaleY;

    // XForm tx,ty is the CENTER of the bitmap - convert to top-left
    let x = xform.tx - displayWidth / 2;
    let y = xform.ty - displayHeight / 2;

    // Flip Y if MirrorY is true
    if (mirrorY) {
        y = canvasHeight - (y + displayHeight);
    }

    // LBRN stores raw base64 without prefix - add it
    // Detect format from data or default to PNG
    let rasterFormat: 'png' | 'jpg' | 'bmp' = 'png';
    let dataUri = `data:image/png;base64,${base64Data}`;

    // Check if this is a JPEG (JPEG magic bytes start with /9j in base64)
    if (base64Data.startsWith('/9j')) {
        rasterFormat = 'jpg';
        dataUri = `data:image/jpeg;base64,${base64Data}`;
    }

    // Extract image processing settings for round-trip preservation
    const gamma = parseFloat(element.getAttribute('Gamma') || '1');
    const contrast = parseFloat(element.getAttribute('Contrast') || '0');
    const brightness = parseFloat(element.getAttribute('Brightness') || '0');

    // Rasters only support engrave operation
    const cutSetting = cutSettings.get(cutIndex);

    return {
        type: 'raster',
        x: parseFloat(x.toFixed(2)),
        y: parseFloat(y.toFixed(2)),
        width: parseFloat(displayWidth.toFixed(2)),
        height: parseFloat(displayHeight.toFixed(2)),
        rotation: 0,  // TODO: extract rotation from XForm if needed
        orderIndex,
        fieldId: undefined,
        // Raster-specific fields
        rasterData: dataUri,
        rasterFormat,
        rasterOriginalWidth: Math.round(w),
        rasterOriginalHeight: Math.round(h),
        // Rasters only support engrave
        score: false,
        engrave: true,
        cut: false,
        engraveColor: cutSetting?.name ? (LIGHTBURN_COLORS[cutSetting.name] || LBRN_COLOR_BLACK) : LBRN_COLOR_BLACK,
        // Store LightBurn-specific data for round-trip
        lbrnCutIndex: cutIndex,
        // Store original LBRN image settings in originalXcsData for preservation
        originalXcsData: {
            type: 'BITMAP' as const,
            lbrnImageSettings: {
                gamma,
                contrast,
                brightness,
            },
        },
    } as Omit<TemplateShape, 'id' | 'templateId'>;
}


// ============================================================
// Path Data Collection for Combining
// ============================================================

interface PathData {
    svgPath: string;
    bounds: PathBounds;
    cutIndex: number;
    isText?: boolean;  // Flag to identify text shapes for proper labeling
}

// ============================================================
// Compound Path Combining (for shapes with holes)
// ============================================================

/**
 * Combine multiple PathData entries into a single compound graphic
 * Uses fill-rule="evenodd" to properly render holes/cutouts
 */
function createCombinedPathGraphic(
    paths: PathData[],
    cutSettings: Map<number, LbrnCutSetting>,
    orderIndex: number
): Omit<TemplateShape, 'id' | 'templateId'> | null {
    if (paths.length === 0) return null;

    // Combine all path d strings into one
    const combinedD = paths.map(p => p.svgPath).join(' ');

    // Calculate combined bounds
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of paths) {
        minX = Math.min(minX, p.bounds.minX);
        minY = Math.min(minY, p.bounds.minY);
        maxX = Math.max(maxX, p.bounds.maxX);
        maxY = Math.max(maxY, p.bounds.maxY);
    }

    const width = maxX - minX;
    const height = maxY - minY;

    if (width <= 0 || height <= 0) return null;

    // Use thin stroke for stroke rendering
    const minStroke = 0.3;
    const proportional = Math.min(width, height) * 0.003;
    const strokeWidthNum = Math.max(minStroke, proportional);
    const strokeWidth = formatCoord(strokeWidthNum);

    // Add padding for stroke clipping
    const padding = strokeWidthNum * 2;
    const paddedX = minX - padding;
    const paddedY = minY - padding;
    const paddedWidth = width + padding * 2;
    const paddedHeight = height + padding * 2;

    const vbMinX = formatCoord(paddedX);
    const vbMinY = formatCoord(paddedY);
    const vbWidth = formatCoord(paddedWidth);
    const vbHeight = formatCoord(paddedHeight);

    // Get CutSetting to determine if this is Scan (fill) or Cut (stroke)
    const cutIndex = paths[0].cutIndex;
    const cutSetting = cutSettings.get(cutIndex);
    const isScanOperation = cutSetting?.type === 'Scan' || cutSetting?.type === 'Image';

    // Create SVG with combined path
    // For Scan operations: use fill with evenodd rule (properly renders holes)
    // For Cut operations: use stroke only
    let svgContent: string;
    if (isScanOperation) {
        // Fill operation - use evenodd for compound paths
        svgContent = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${vbMinX} ${vbMinY} ${vbWidth} ${vbHeight}" width="${vbWidth}" height="${vbHeight}"><path d="${combinedD}" fill="currentColor" fill-rule="evenodd" stroke="none"/></svg>`;
    } else {
        // Cut/Line operation - stroke only
        svgContent = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${vbMinX} ${vbMinY} ${vbWidth} ${vbHeight}" width="${vbWidth}" height="${vbHeight}"><path d="${combinedD}" fill="none" stroke="currentColor" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    }

    // Get operations from CutIndex
    const ops = mapCutSettingToOperations(cutSetting, cutSetting?.name || `C${cutIndex.toString().padStart(2, '0')}`);

    // Check if any paths are text
    const hasText = paths.some(p => p.isText);

    const shape: Omit<TemplateShape, 'id' | 'templateId'> & { lbrnCutIndex?: number } = {
        type: 'graphic',
        x: paddedX,
        y: paddedY,
        width: paddedWidth,
        height: paddedHeight,
        rotation: 0,
        orderIndex,
        fieldId: undefined,
        svgData: svgContent,
        lbrnCutIndex: cutIndex,  // Store CutIndex for process settings import
        ...ops,
    };

    if (hasText) {
        (shape as any).originalXcsData = { type: 'TEXT' };
    }

    return shape as Omit<TemplateShape, 'id' | 'templateId'>;
}

function collectPathData(
    element: Element,
    mirrorY: boolean,
    canvasHeight: number,
    overrideCutIndex?: number,
    isText?: boolean
): PathData | null {
    const cutIndex = overrideCutIndex ?? parseInt(element.getAttribute('CutIndex') || '0');
    const xform = parseXForm(element);

    // Parse vertices
    const vertices: LbrnVertex[] = [];
    element.querySelectorAll(':scope > V').forEach(v => {
        vertices.push({
            vx: parseFloat(v.getAttribute('vx') || '0'),
            vy: parseFloat(v.getAttribute('vy') || '0'),
            c0x: v.hasAttribute('c0x') ? parseFloat(v.getAttribute('c0x')!) : undefined,
            c0y: v.hasAttribute('c0y') ? parseFloat(v.getAttribute('c0y')!) : undefined,
            c1x: v.hasAttribute('c1x') ? parseFloat(v.getAttribute('c1x')!) : undefined,
            c1y: v.hasAttribute('c1y') ? parseFloat(v.getAttribute('c1y')!) : undefined,
        });
    });

    // Parse primitives
    const primitives: LbrnPrimitive[] = [];
    element.querySelectorAll(':scope > P').forEach(p => {
        primitives.push({
            type: (p.getAttribute('T') || 'L') as 'L' | 'B' | 'Q',
            p0: parseInt(p.getAttribute('p0') || '0'),
            p1: parseInt(p.getAttribute('p1') || '0'),
        });
    });

    if (vertices.length === 0 || primitives.length === 0) return null;

    // Convert to SVG path in world coordinates
    const svgPath = lbrnPathToSvg(vertices, primitives, xform, mirrorY, canvasHeight);
    const bounds = calculatePathBounds(vertices, xform, mirrorY, canvasHeight);

    if (bounds.width === 0 || bounds.height === 0) return null;

    return { svgPath, bounds, cutIndex, isText };
}

function collectEllipseData(
    element: Element,
    mirrorY: boolean,
    canvasHeight: number
): PathData | null {
    const rx = parseFloat(element.getAttribute('Rx') || '0');
    const ry = parseFloat(element.getAttribute('Ry') || '0');
    const cutIndex = parseInt(element.getAttribute('CutIndex') || '0');

    if (rx === 0 || ry === 0) return null;

    const xform = parseXForm(element);

    // Apply scaling from XForm
    const scaledRx = rx * Math.abs(xform.a || 1);
    const scaledRy = ry * Math.abs(xform.d || 1);

    let cx = xform.tx;
    let cy = xform.ty;

    if (mirrorY) {
        cy = canvasHeight - cy;
    }

    // Generate ellipse as SVG path in world coordinates
    const svgPath = ellipseToPath(cx, cy, scaledRx, scaledRy);

    const bounds: PathBounds = {
        minX: cx - scaledRx,
        minY: cy - scaledRy,
        maxX: cx + scaledRx,
        maxY: cy + scaledRy,
        width: scaledRx * 2,
        height: scaledRy * 2,
    };

    return { svgPath, bounds, cutIndex };
}

function collectPolygonData(
    element: Element,
    mirrorY: boolean,
    canvasHeight: number
): PathData | null {
    const rx = parseFloat(element.getAttribute('Rx') || '0');
    const ry = parseFloat(element.getAttribute('Ry') || '0');
    const n = parseInt(element.getAttribute('N') || '0');
    const cutIndex = parseInt(element.getAttribute('CutIndex') || '0');

    if (rx === 0 || ry === 0 || n < 3) return null;

    const xform = parseXForm(element);

    // Apply scaling from XForm
    const scaledRx = rx * Math.abs(xform.a || 1);
    const scaledRy = ry * Math.abs(xform.d || 1);

    let cx = xform.tx;
    let cy = xform.ty;

    if (mirrorY) {
        cy = canvasHeight - cy;
    }

    // Generate polygon as SVG path in world coordinates
    const svgPath = polygonToPath(cx, cy, scaledRx, scaledRy, n);
    console.log(`[LBRN Import] Polygon N=${n}, vertices generated:`, svgPath.split(/[MLZ]/).filter(s => s.trim()).length);

    const bounds: PathBounds = {
        minX: cx - scaledRx,
        minY: cy - scaledRy,
        maxX: cx + scaledRx,
        maxY: cy + scaledRy,
        width: scaledRx * 2,
        height: scaledRy * 2,
    };

    return { svgPath, bounds, cutIndex };
}

function collectStarData(
    element: Element,
    mirrorY: boolean,
    canvasHeight: number
): PathData | null {
    const rx = parseFloat(element.getAttribute('Rx') || '0');
    const ry = parseFloat(element.getAttribute('Ry') || '0');
    const n = parseInt(element.getAttribute('N') || '0');
    const ra = parseFloat(element.getAttribute('Ra') || '0.5');
    const cutIndex = parseInt(element.getAttribute('CutIndex') || '0');

    if (rx === 0 || ry === 0 || n < 3) return null;

    const xform = parseXForm(element);

    // Apply scaling from XForm
    const scaledRx = rx * Math.abs(xform.a || 1);
    const scaledRy = ry * Math.abs(xform.d || 1);

    let cx = xform.tx;
    let cy = xform.ty;

    if (mirrorY) {
        cy = canvasHeight - cy;
    }

    // Generate star as SVG path in world coordinates
    const svgPath = starToPath(cx, cy, scaledRx, scaledRy, n, ra);

    const bounds: PathBounds = {
        minX: cx - scaledRx,
        minY: cy - scaledRy,
        maxX: cx + scaledRx,
        maxY: cy + scaledRy,
        width: scaledRx * 2,
        height: scaledRy * 2,
    };

    return { svgPath, bounds, cutIndex };
}

function collectDualStarData(
    element: Element,
    mirrorY: boolean,
    canvasHeight: number
): PathData | null {
    const rx = parseFloat(element.getAttribute('Rx') || '0');
    const ry = parseFloat(element.getAttribute('Ry') || '0');
    const n = parseInt(element.getAttribute('N') || '0');
    const ra = parseFloat(element.getAttribute('Ra') || '0.5');
    const ra2 = parseFloat(element.getAttribute('Ra2') || '0.7');
    const cutIndex = parseInt(element.getAttribute('CutIndex') || '0');

    if (rx === 0 || ry === 0 || n < 3) return null;

    const xform = parseXForm(element);

    // Apply scaling from XForm
    const scaledRx = rx * Math.abs(xform.a || 1);
    const scaledRy = ry * Math.abs(xform.d || 1);

    let cx = xform.tx;
    let cy = xform.ty;

    if (mirrorY) {
        cy = canvasHeight - cy;
    }

    // Generate dual star as SVG path in world coordinates
    const svgPath = dualStarToPath(cx, cy, scaledRx, scaledRy, n, ra, ra2);
    console.log(`[LBRN Import] DualStar N=${n}, vertices generated: ${n * 4}`, { ra, ra2, cutIndex });

    const bounds: PathBounds = {
        minX: cx - scaledRx,
        minY: cy - scaledRy,
        maxX: cx + scaledRx,
        maxY: cy + scaledRy,
        width: scaledRx * 2,
        height: scaledRy * 2,
    };

    return { svgPath, bounds, cutIndex };
}

// ============================================================
// Main Shape Parser
// ============================================================

function parseShapes(
    doc: Document,
    cutSettings: Map<number, LbrnCutSetting>,
    mirrorY: boolean,
    canvasHeight: number,
    warnings: string[]
): Omit<TemplateShape, 'id' | 'templateId'>[] {
    const shapes: Omit<TemplateShape, 'id' | 'templateId'>[] = [];
    let orderIndex = 0;

    // Collect all paths as individual shapes (not merged by CutIndex)
    const allPaths: PathData[] = [];

    // Helper to add path data
    const addPathData = (data: PathData | null) => {
        if (!data) return;
        allPaths.push(data);
    };

    // Helper to process group children
    const processGroup = (element: Element) => {
        element.querySelectorAll(':scope > Shape').forEach(child => {
            const type = child.getAttribute('Type');
            switch (type) {
                case 'Rect':
                    const rect = parseRectShape(child, cutSettings, mirrorY, canvasHeight, orderIndex);
                    if (rect) {
                        shapes.push(rect);
                        orderIndex++;
                    }
                    break;
                case 'Ellipse':
                    addPathData(collectEllipseData(child, mirrorY, canvasHeight));
                    break;
                case 'Polygon':
                    addPathData(collectPolygonData(child, mirrorY, canvasHeight));
                    break;
                case 'Star':
                    addPathData(collectStarData(child, mirrorY, canvasHeight));
                    break;
                case 'DualStar':
                    addPathData(collectDualStarData(child, mirrorY, canvasHeight));
                    break;
                case 'Path':
                    addPathData(collectPathData(child, mirrorY, canvasHeight));
                    break;
                case 'Text': {
                    // Text shapes should be collected like paths if they have BackupPath
                    const textBackupPath = child.querySelector(':scope > BackupPath');
                    const textCutIndex = parseInt(child.getAttribute('CutIndex') || '0');
                    if (textBackupPath) {
                        addPathData(collectPathData(textBackupPath, mirrorY, canvasHeight, textCutIndex, true));
                    } else {
                        const text = child.getAttribute('Str') || '';
                        const fontAttr = child.getAttribute('Font') || '';
                        const fontFamily = fontAttr.split(',')[0] || 'Unknown';
                        warnings.push(`Text "${text}" uses font "${fontFamily}" - no BackupPath available, skipped`);
                    }
                    break;
                }
                case 'Bitmap': {
                    // Bitmap images - parse as raster shapes
                    const bitmap = parseBitmapShape(child, cutSettings, mirrorY, canvasHeight, orderIndex);
                    if (bitmap) {
                        shapes.push(bitmap);
                        orderIndex++;
                    }
                    break;
                }
                case 'Group':
                    processGroup(child);
                    break;
            }
        });
    };

    // Only select direct children of root to avoid nested shapes
    const root = doc.documentElement;
    root.querySelectorAll(':scope > Shape').forEach(element => {
        const type = element.getAttribute('Type');

        switch (type) {
            case 'Rect':
                // Rects stay separate (they're field placeholders)
                const rect = parseRectShape(element, cutSettings, mirrorY, canvasHeight, orderIndex);
                if (rect) {
                    shapes.push(rect);
                    orderIndex++;
                }
                break;
            case 'Ellipse':
                // Collect ellipse data for combining
                addPathData(collectEllipseData(element, mirrorY, canvasHeight));
                break;
            case 'Polygon':
                // Collect polygon data for combining
                addPathData(collectPolygonData(element, mirrorY, canvasHeight));
                break;
            case 'Star':
                // Collect star data for combining
                addPathData(collectStarData(element, mirrorY, canvasHeight));
                break;
            case 'DualStar':
                // Collect dual star data for combining
                addPathData(collectDualStarData(element, mirrorY, canvasHeight));
                break;
            case 'Path':
                // Collect path data for combining
                addPathData(collectPathData(element, mirrorY, canvasHeight));
                break;
            case 'Text': {
                // Check for BackupPath (pre-rendered path data)
                const backupPath = element.querySelector(':scope > BackupPath');
                const textCutIndex = parseInt(element.getAttribute('CutIndex') || '0');
                if (backupPath) {
                    addPathData(collectPathData(backupPath, mirrorY, canvasHeight, textCutIndex, true));
                } else {
                    const text = element.getAttribute('Str') || '';
                    const fontAttr = element.getAttribute('Font') || '';
                    const fontFamily = fontAttr.split(',')[0] || 'Unknown';
                    warnings.push(`Text "${text}" uses font "${fontFamily}" - no BackupPath available, skipped`);
                }
                break;
            }
            case 'Bitmap': {
                // Bitmap images - parse as raster shapes
                const bitmap = parseBitmapShape(element, cutSettings, mirrorY, canvasHeight, orderIndex);
                if (bitmap) {
                    shapes.push(bitmap);
                    orderIndex++;
                }
                break;
            }
            case 'Group':
                processGroup(element);
                break;
            default:
                if (type) {
                    warnings.push(`Unknown shape type: ${type}`);
                }
        }
    });

    // Group paths by CutIndex to combine compound vectors (shapes with holes)
    // This ensures proper fill-rule="evenodd" rendering for shapes like donuts/rings
    const pathsByCutIndex = new Map<number, PathData[]>();
    for (const pathData of allPaths) {
        const existing = pathsByCutIndex.get(pathData.cutIndex) || [];
        existing.push(pathData);
        pathsByCutIndex.set(pathData.cutIndex, existing);
    }

    // Create combined graphics for each CutIndex group
    // Multiple paths with same CutIndex become a single compound graphic
    for (const [, paths] of pathsByCutIndex) {
        const graphic = createCombinedPathGraphic(paths, cutSettings, orderIndex);
        if (graphic) {
            shapes.push(graphic);
            orderIndex++;
        }
    }

    return shapes;
}

// ============================================================
// Scaling Utility
// ============================================================

export function scaleLbrnShapesToFit(
    shapes: Omit<TemplateShape, 'id' | 'templateId'>[],
    originalWidth: number,
    originalHeight: number,
    targetWidth: number,
    targetHeight: number
): Omit<TemplateShape, 'id' | 'templateId'>[] {
    if (originalWidth === 0 || originalHeight === 0) return shapes;

    const scaleX = targetWidth / originalWidth;
    const scaleY = targetHeight / originalHeight;
    const scale = Math.min(scaleX, scaleY);

    return shapes.map(shape => ({
        ...shape,
        x: shape.x * scale,
        y: shape.y * scale,
        width: shape.width * scale,
        height: shape.height * scale,
    }));
}

// ============================================================
// Main Parse Function
// ============================================================

/**
 * Parse LBRN file content into ImportResult
 *
 * LBRN Coordinate System:
 * - When MirrorY="True": Origin is bottom-left, Y increases upward
 * - We need to flip Y coordinates to match our canvas (top-left origin, Y-down)
 *
 * Import Strategy:
 * 1. Parse CutSettings for operation mapping
 * 2. Calculate document bounds first to determine flip parameters
 * 3. Parse all shapes with Y-flip applied during generation
 * 4. Offset to (0,0)
 */
export async function parseLbrnFile(
    content: string,
    _workAreaWidth: number,  // Reserved - no longer used for auto-scaling
    _workAreaHeight: number  // Reserved - shapes keep original LightBurn coordinates
): Promise<LbrnImportResult> {
    const warnings: string[] = [];
    let originalWidth = 0;
    let originalHeight = 0;
    let needsScaling = false;

    try {
        // Parse XML
        const parser = new DOMParser();
        const doc = parser.parseFromString(content, 'text/xml');

        // Check for parse errors
        const parseError = doc.querySelector('parsererror');
        if (parseError) {
            return {
                shapes: [],
                needsScaling: false,
                originalSize: { width: 0, height: 0 },
                svgContent: '',
                warnings: ['Failed to parse LBRN: Invalid XML'],
            };
        }

        // Parse CutSettings first
        const cutSettings = parseCutSettings(doc);

        // Parse shapes - no Y-flip needed since LightBurn path data renders correctly as-is
        const shapes = parseShapes(doc, cutSettings, false, 0, warnings);

        if (shapes.length === 0) {
            return {
                shapes: [],
                needsScaling: false,
                originalSize: { width: 0, height: 0 },
                svgContent: '',
                warnings: warnings.length > 0 ? warnings : ['No shapes found in LBRN file'],
            };
        }

        // Calculate bounding box for size tracking (but don't offset shapes!)
        // Shapes should maintain their original LightBurn coordinates
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const shape of shapes) {
            minX = Math.min(minX, shape.x);
            minY = Math.min(minY, shape.y);
            maxX = Math.max(maxX, shape.x + shape.width);
            maxY = Math.max(maxY, shape.y + shape.height);
        }

        originalWidth = maxX - minX;
        originalHeight = maxY - minY;
        // Don't auto-scale - let shapes stay at their original positions
        // even if outside the work area
        needsScaling = false;

        // Return shapes with original coordinates - NO offsetting!
        // This preserves positions of shapes intentionally placed outside the work area
        return {
            shapes: shapes,
            needsScaling,
            originalSize: { width: originalWidth, height: originalHeight },
            svgContent: '',
            warnings,
            cutSettings,  // Include parsed CutSettings for process settings import
        };
    } catch (error) {
        return {
            shapes: [],
            needsScaling: false,
            originalSize: { width: 0, height: 0 },
            svgContent: '',
            warnings: [`Failed to parse LBRN: ${error instanceof Error ? error.message : 'Unknown error'}`],
        };
    }
}
